import React from 'react';
import { Check, Sparkles, Trash2, Bookmark } from 'lucide-react';
import type { ThemePreset, SavedCustomTheme } from '../../types/theme';
import { THEME_PRESETS } from '../../lib/themePresets';

interface PresetSelectorProps {
  activePresetId: string | null;
  savedCustomThemes: SavedCustomTheme[];
  onSelectPreset: (preset: ThemePreset) => void;
  onSelectSavedTheme: (saved: SavedCustomTheme) => void;
  onDeleteSavedTheme: (id: string) => void;
}

export const PresetSelector: React.FC<PresetSelectorProps> = ({
  activePresetId,
  savedCustomThemes,
  onSelectPreset,
  onSelectSavedTheme,
  onDeleteSavedTheme,
}) => {
  return (
    <div className="space-y-4">
      {/* Standard Curated Presets Grid */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5 font-bold text-xs text-foreground uppercase tracking-wider">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span>Curated Theme Presets</span>
          </div>
          <span className="text-[11px] text-muted-foreground">Pick a starting base to customize</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {THEME_PRESETS.map((preset) => {
            const isSelected = activePresetId === preset.id;

            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => onSelectPreset(preset)}
                className={`relative flex flex-col p-3.5 rounded-2xl border text-left transition-all duration-200 group ${
                  isSelected
                    ? 'border-primary bg-primary/[0.04] ring-2 ring-primary/30 shadow-md scale-[1.01]'
                    : 'border-border/80 bg-card hover:border-primary/40 hover:bg-muted/30 hover:shadow-xs'
                }`}
              >
                {isSelected && (
                  <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-xs">
                    <Check className="h-3 w-3 stroke-[3]" />
                  </div>
                )}

                {/* Color preview palette chips */}
                <div className="flex items-center gap-1.5 mb-2.5">
                  <div
                    className="w-5 h-5 rounded-lg border border-black/10 shadow-2xs"
                    style={{ backgroundColor: preset.previewColors.sidebar }}
                    title="Sidebar color"
                  />
                  <div
                    className="w-5 h-5 rounded-lg border border-black/10 shadow-2xs"
                    style={{ backgroundColor: preset.previewColors.chat }}
                    title="Chat background"
                  />
                  <div
                    className="w-5 h-5 rounded-lg border border-black/10 shadow-2xs"
                    style={{ backgroundColor: preset.previewColors.primary }}
                    title="Primary accent"
                  />
                  <div
                    className="w-5 h-5 rounded-lg border border-black/10 shadow-2xs"
                    style={{ backgroundColor: preset.previewColors.card }}
                    title="Card background"
                  />
                </div>

                <div className="font-bold text-xs text-foreground group-hover:text-primary transition truncate">
                  {preset.name}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">
                  {preset.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Saved Custom Themes Section */}
      {savedCustomThemes.length > 0 && (
        <div className="pt-2">
          <div className="flex items-center gap-1.5 font-bold text-xs text-foreground uppercase tracking-wider mb-2.5">
            <Bookmark className="h-3.5 w-3.5 text-primary" />
            <span>My Saved Custom Themes ({savedCustomThemes.length})</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {savedCustomThemes.map((saved) => (
              <div
                key={saved.id}
                className="relative flex items-center justify-between p-3 rounded-2xl border border-border/80 bg-card hover:border-primary/40 transition group"
              >
                <button
                  type="button"
                  onClick={() => onSelectSavedTheme(saved)}
                  className="flex-1 text-left min-w-0 pr-2"
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div
                      className="w-4 h-4 rounded-md border border-black/10"
                      style={{ backgroundColor: saved.tokens.primarySidebarBackground }}
                    />
                    <div
                      className="w-4 h-4 rounded-md border border-black/10"
                      style={{ backgroundColor: saved.tokens.chatBackground }}
                    />
                    <div
                      className="w-4 h-4 rounded-md border border-black/10"
                      style={{ backgroundColor: saved.tokens.primaryAccent }}
                    />
                  </div>
                  <div className="font-bold text-xs text-foreground truncate">{saved.name}</div>
                  <span className="text-[10px] text-muted-foreground">
                    Saved {new Date(saved.createdAt).toLocaleDateString()}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => onDeleteSavedTheme(saved.id)}
                  title="Delete saved theme"
                  aria-label="Delete saved theme"
                  className="p-1.5 rounded-xl hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition opacity-60 group-hover:opacity-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
